import { CSSProperties, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BRIDGE_CHANNEL, nativeRequest } from './nativeBridge';
import { chatEndpoint } from './chatEndpoint';
import { fitChatPanel } from './fitChatViewport';
import './BroadcastChatComposer.css';
import ChatModeratedText from '../../Component/UI/ChatModeratedText';
import { handleChatComposerKeyDown } from '../../Component/Support/chatComposerKeyboard';

type Message = { id?: string; Content: string; SendTime?: string; Attributes?: { type?: string; moderation?: string; forbiddenSpans?: string }; Sender?: { UserId?: string; Attributes?: { displayName?: string } } };
export default function BroadcastChat({ seq, enabled = true, children, onPurchaseNotification, panel = false, notices, headerControls, fontScale = 1 }: { seq: number; enabled?: boolean; children?: React.ReactNode; onPurchaseNotification: (content: string) => void; panel?: boolean; notices?: React.ReactNode; headerControls?: React.ReactNode; fontScale?: number }) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [connected, setConnected] = useState(false);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const pending = useRef<{ requestId: string; content: string; timer: ReturnType<typeof setTimeout> } | null>(null);
  const sendSequence = useRef(0);
  const socket = useRef<WebSocket | null>(null);
  const list = useRef<HTMLDivElement>(null);
  const bottom = useRef(true);
  const finishSend = useCallback((success: boolean, failure?: string) => {
    const request = pending.current;
    if (!request) return;
    clearTimeout(request.timer); pending.current = null; setSending(false);
    if (success) setDraft(current => current.trim() === request.content ? '' : current);
    else if (failure) setError(failure);
  }, []);
  useEffect(() => { setDraft(''); }, [seq]);
  useEffect(() => {
    setConnected(false); setError(''); setSending(false);
    if (!enabled) return;
    let disposed = false, connecting = false, attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    let handshake: ReturnType<typeof setTimeout>;
    const received = new Set<string>();
    const reconnect = () => {
      clearTimeout(timer);
      if (!disposed) timer = setTimeout(connect, Math.min(1000 * 2 ** Math.min(attempts++, 3), 5000));
    };
    const connect = async () => {
      if (disposed || connecting) return;
      connecting = true;
      try {
        const ticket = await nativeRequest<{ endpoint: string; token: string }>('chat-ticket');
        if (disposed) return;
        const ws = new WebSocket(chatEndpoint(ticket.endpoint), ticket.token);
        socket.current = ws;
        handshake = setTimeout(() => {
          if (disposed || socket.current !== ws) return;
          socket.current = null; ws.close();
          setConnected(false); setError('채팅 서버에 연결하지 못했습니다. 다시 연결 중입니다.'); reconnect();
        }, 8000);
        ws.onopen = () => { if (!disposed && socket.current === ws) { clearTimeout(handshake); attempts = 0; setConnected(true); setError(''); } };
        ws.onmessage = event => {
          if (disposed || socket.current !== ws) return;
          try {
            const data = JSON.parse(event.data);
            if (data.type === 'ack') { if (data.requestId === pending.current?.requestId) finishSend(true); return; }
            if (data.type === 'error') { const failure = data.message || '채팅을 보내지 못했습니다.'; if (data.requestId === pending.current?.requestId) finishSend(false, failure); else setError(failure); return; }
            const batch: Message[] = data.type === 'batch' ? data.messages : [data];
            if (!Array.isArray(batch)) return;
            const incoming: Message[] = [];
            for (const message of batch) {
              if (typeof message?.Content !== 'string' || (message.id && received.has(message.id))) continue;
              if (message.id) received.add(message.id);
              if (['muted', 'forbidden-word'].includes(message.Attributes?.moderation || '')) continue;
              if (message.Attributes?.type?.toLowerCase() === 'buy') {
                if (!message.Attributes?.moderation) onPurchaseNotification(message.Content);
              }
              else incoming.push(message);
            }
            if (received.size > 2000) received.clear();
            if (incoming.length) setMessages(current => [...current, ...incoming].slice(-200));
          } catch { ws.close(); }
        };
        ws.onerror = () => {};
        ws.onclose = () => {
          if (disposed || socket.current !== ws) return;
          clearTimeout(handshake); socket.current = null; setConnected(false);
          finishSend(false);
          setError('채팅 연결이 끊겼습니다. 다시 연결 중입니다.'); reconnect();
        };
      } catch (failure) { if (!disposed) {
        setConnected(false); setError(failure instanceof Error ? failure.message : '채팅을 연결하지 못했습니다.'); reconnect();
      } }
      finally { connecting = false; }
    };
    const resume = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        if (data.channel !== BRIDGE_CHANNEL || data.type !== 'resume') return;
        const previous = socket.current; socket.current = null; previous?.close();
        finishSend(false);
        setConnected(false); clearTimeout(timer); clearTimeout(handshake); void connect();
      } catch { /* Other window messages. */ }
    };
    setMessages([]); bottom.current = true;
    void connect(); window.addEventListener('message', resume);
    return () => {
      disposed = true; clearTimeout(timer); clearTimeout(handshake); window.removeEventListener('message', resume);
      if (pending.current) { clearTimeout(pending.current.timer); pending.current = null; }
      socket.current?.close(); socket.current = null;
    };
  }, [seq, enabled, retry, onPurchaseNotification, finishSend]);
  useLayoutEffect(() => {
    if (!list.current) return;
    fitChatPanel(list.current, bottom.current);
  }, [messages, panel, fontScale]);
  useEffect(() => {
    let frame = 0;
    const resize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (list.current) fitChatPanel(list.current, bottom.current);
      });
    };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(resize) : null;
    if (list.current) observer?.observe(list.current, { box: 'border-box' });
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(frame); observer?.disconnect(); window.removeEventListener('resize', resize); };
  }, [panel]);
  const send = (event: React.FormEvent) => {
    event.preventDefault();
    const content = draft.trim(), ws = socket.current;
    if (!content || content.length > 100 || !enabled || !connected || !ws || ws.readyState !== WebSocket.OPEN || pending.current) return;
    const requestId = `seller-${Date.now()}-${++sendSequence.current}`;
    const timer = setTimeout(() => finishSend(false, '채팅 전송을 확인하지 못했습니다. 다시 시도해주세요.'), 10000);
    pending.current = { requestId, content, timer }; setSending(true); setError(''); bottom.current = true;
    try { ws.send(JSON.stringify({ action: 'SEND_MESSAGE', requestId, content, Attributes: { type: 'user' } })); }
    catch { finishSend(false, '채팅을 보내지 못했습니다. 다시 시도해주세요.'); }
  };
  return <section className={`studio-chat ${panel ? 'studio-chat--panel' : ''}`} aria-label={panel ? '라이브 채팅' : '방송 채팅'} style={{ '--studio-chat-font-scale': fontScale } as CSSProperties}>
    <div className="studio-chat-stream">
    {notices && <div className="studio-chat-notices">{notices}</div>}
    {(panel || headerControls) && <header className="studio-chat__header">{panel && <h2>라이브 채팅</h2>}{headerControls}</header>}
    <div className="studio-chat__messages" ref={list} role="log" aria-live="polite" onScroll={event => {
      const node = event.currentTarget; bottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 8;
    }}>{messages.map((message, index) => {
      const chatType = message.Attributes?.type?.toLowerCase() === 'coupon' ? 'coupon' : ['master', 'linkcampus', 'testY', 'system'].includes(message.Sender?.UserId ?? '') ? 'manage' : message.Attributes?.type?.toLowerCase();
      return <p key={message.id ?? index} className={`${chatType || 'user'}${message.Attributes?.moderation === 'muted' ? ' muted' : ''}${message.Attributes?.moderation === 'forbidden-word' ? ' forbidden' : ''}`}>
      <span className="studio-chat__body">
      {chatType !== 'coupon' && !(panel && chatType === 'manage') && <b>{message.Sender?.Attributes?.displayName || message.Sender?.UserId || '시청자'}</b>}
      <ChatModeratedText content={message.Content} spans={message.Attributes?.forbiddenSpans} />
      </span>
      {panel && message.SendTime && Number.isFinite(Date.parse(message.SendTime)) && <time dateTime={message.SendTime}>{new Intl.DateTimeFormat('ko-KR', {timeZone: 'Asia/Seoul', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'}).format(new Date(message.SendTime))}</time>}
    </p>; })}</div>
    </div>
    {children}
    {error && <p className="studio-chat__error" role="alert">{error}</p>}
    {enabled && !connected && <p className="studio-chat__connection" role="status">{error ? <button type="button" onClick={() => setRetry(value => value + 1)}>채팅 다시 연결</button> : '채팅 연결 중…'}</p>}
    <form className="studio-chat__composer" aria-label="채팅 작성" onSubmit={send}>
      <textarea aria-label="채팅 메시지" placeholder="채팅 메시지를 입력하세요" value={draft} rows={Math.min(3, draft.split('\n').length)} maxLength={100} autoComplete="off" enterKeyHint="send" disabled={!enabled} onChange={event => setDraft(event.target.value)}
        onKeyDown={event => handleChatComposerKeyDown(event, setDraft, () => send(event))} />
      <button type="submit" aria-label="채팅 보내기" onPointerDown={event => event.preventDefault()} onPointerUp={event => {
        if (event.button > 0) return;
        event.preventDefault();
        event.currentTarget.form?.requestSubmit(event.currentTarget);
      }} disabled={!enabled || !connected || sending || !draft.trim()}>{sending ? '전송 중' : '전송'}</button>
    </form>
  </section>;
}
